import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Info,
  OctagonAlert,
} from "lucide-react";
import {
  classify,
  visibleQuestions,
  isAnswered,
  daysUntil,
  PACKS,
} from "../lib/audit";
import { detectLang, setLang as persistLang, pickLang, UI, tt } from "../lib/i18n";
import "../App.css";

const CAL_URL = "https://cal.com/vishwanath-sukumar/25min";
const SUBMIT_ENDPOINT = "/api/audit-submit";
const SUBSCRIBE_EMAIL = "vishwa363@gmail.com";

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const Wordmark = () => (
  <Link
    to="/"
    data-testid="brand-wordmark"
    className="inline-flex items-baseline gap-2 select-none"
    aria-label="Adheran — home"
  >
    <span aria-hidden="true" className="inline-block h-2 w-2 rounded-sm" style={{ background: "var(--blue)" }} />
    <span className="text-[15px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--ink)" }}>
      Adheran
    </span>
  </Link>
);

function LangToggle({ lang, onChange }) {
  return (
    <div
      role="group"
      aria-label="Language"
      className="inline-flex items-center overflow-hidden rounded-full border text-[12px] font-semibold tracking-[0.14em] uppercase"
      style={{ borderColor: "var(--line)", background: "white" }}
    >
      {["en", "de"].map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onChange(code)}
            data-testid={`lang-toggle-${code}`}
            className="px-3 py-1.5 transition-colors"
            style={{
              background: active ? "var(--ink)" : "transparent",
              color: active ? "white" : "var(--muted)",
            }}
          >
            {code}
          </button>
        );
      })}
    </div>
  );
}

function Nav({ lang, onLangChange }) {
  return (
    <header
      data-testid="assessment-header"
      className="sticky top-0 z-40 w-full border-b backdrop-blur-md"
      style={{ background: "rgba(255,255,255,0.78)", borderColor: "var(--line)" }}
    >
      <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4 md:px-10">
        <Wordmark />
        <div className="flex items-center gap-3 sm:gap-5">
          <LangToggle lang={lang} onChange={onLangChange} />
          <Link
            to="/"
            data-testid="back-home"
            className="link-u inline-flex items-center gap-2 text-sm"
            style={{ color: "var(--muted)" }}
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">{tt(UI, "back_home", lang)}</span>
          </Link>
        </div>
      </div>
    </header>
  );
}

function ProgressBar({ current, total, lang }) {
  const pct = Math.round(((current + 1) / total) * 100);
  return (
    <div className="mt-2" aria-hidden="true">
      <div className="h-1 w-full rounded-full" style={{ background: "var(--line)" }}>
        <div
          className="h-1 rounded-full transition-all duration-300"
          style={{ background: "var(--blue)", width: `${pct}%` }}
        />
      </div>
      <p className="mt-2 text-[12px] font-medium tracking-[0.18em] uppercase" style={{ color: "var(--muted-2)" }}>
        {tt(UI, "question_of", lang, { n: current + 1, total })}
      </p>
    </div>
  );
}

function SingleChoice({ question, value, onChange, lang }) {
  return (
    <div className="space-y-3">
      {question.options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`q-${question.id}-${opt.value}`}
            onClick={() => onChange(opt.value)}
            className="block w-full rounded-xl border p-4 text-left transition-all hover:shadow-sm md:p-5"
            style={{
              borderColor: selected ? "var(--ink)" : "var(--line)",
              background: selected ? "var(--tint)" : "white",
              borderWidth: selected ? 2 : 1,
            }}
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2"
                style={{
                  borderColor: selected ? "var(--ink)" : "var(--muted-2)",
                  background: selected ? "var(--ink)" : "transparent",
                }}
              >
                {selected && <span className="h-1.5 w-1.5 rounded-full" style={{ background: "white" }} />}
              </span>
              <div className="flex-1">
                <span className="text-[15.5px] leading-relaxed" style={{ color: "var(--ink)" }}>
                  {pickLang(opt.label, lang)}
                </span>
                {opt.cite && (
                  <span className="mt-1 block text-[12.5px]" style={{ color: "var(--muted-2)" }}>
                    {opt.cite}
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function MultiChoice({ question, value, onChange, lang }) {
  const noneId = question.id === "annex" ? "none_annex" : question.id === "prohibited" ? "none_prohibited" : null;
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    if (noneId && v === noneId) {
      onChange(selected.includes(noneId) ? [] : [noneId]);
      return;
    }
    const without = noneId ? selected.filter((s) => s !== noneId) : selected;
    if (without.includes(v)) onChange(without.filter((s) => s !== v));
    else onChange([...without, v]);
  };

  return (
    <div className="space-y-3">
      {question.options.map((opt) => {
        const isSelected = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`q-${question.id}-${opt.value}`}
            onClick={() => toggle(opt.value)}
            className="block w-full rounded-xl border p-4 text-left transition-all hover:shadow-sm md:p-5"
            style={{
              borderColor: isSelected ? "var(--ink)" : "var(--line)",
              background: isSelected ? "var(--tint)" : "white",
              borderWidth: isSelected ? 2 : 1,
            }}
          >
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[4px] border-2"
                style={{
                  borderColor: isSelected ? "var(--ink)" : "var(--muted-2)",
                  background: isSelected ? "var(--ink)" : "transparent",
                }}
              >
                {isSelected && <Check className="h-3 w-3" style={{ color: "white" }} strokeWidth={3} />}
              </span>
              <div className="flex-1">
                <span className="text-[15.5px] leading-relaxed" style={{ color: "var(--ink)" }}>
                  {pickLang(opt.label, lang)}
                </span>
                {opt.cite && (
                  <span className="mt-1 block text-[12.5px]" style={{ color: "var(--muted-2)" }}>
                    {opt.cite}
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function FreeText({ question, value, onChange, lang }) {
  return (
    <textarea
      data-testid={`q-${question.id}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={pickLang(question.placeholder, lang) || ""}
      rows={4}
      className="w-full rounded-xl border p-4 text-[15.5px] leading-relaxed outline-none transition-all focus:border-2"
      style={{ borderColor: "var(--line)", color: "var(--ink)" }}
    />
  );
}

function QuestionView({ question, answers, onAnswer, onBack, onNext, index, total, canBack, lang }) {
  const value = answers[question.id];
  const answered = isAnswered(question, answers);

  return (
    <section data-testid={`section-question-${question.id}`} className="mx-auto w-full max-w-2xl">
      <ProgressBar current={index} total={total} lang={lang} />
      <h2
        data-testid={`q-${question.id}-text`}
        className="display-h2 balance mt-7 text-2xl sm:text-3xl md:text-[34px]"
        style={{ color: "var(--ink)" }}
      >
        {pickLang(question.text, lang)}
      </h2>
      {question.help && (
        <p className="pretty mt-4 text-[14.5px] leading-relaxed" style={{ color: "var(--muted)" }}>
          {pickLang(question.help, lang)}
        </p>
      )}

      <div className="mt-7">
        {question.type === "single" && (
          <SingleChoice question={question} value={value} onChange={(v) => onAnswer(question.id, v)} lang={lang} />
        )}
        {question.type === "multi" && (
          <MultiChoice question={question} value={value} onChange={(v) => onAnswer(question.id, v)} lang={lang} />
        )}
        {question.type === "text" && (
          <FreeText question={question} value={value} onChange={(v) => onAnswer(question.id, v)} lang={lang} />
        )}
      </div>

      <div className="mt-9 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={!canBack}
          data-testid="audit-back"
          className="btn-ghost btn-ghost--sm self-start disabled:opacity-40"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
          <span>{tt(UI, "back", lang)}</span>
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!answered}
          data-testid="audit-next"
          className="btn-primary disabled:opacity-40"
        >
          <span>{index === total - 1 ? tt(UI, "see_result", lang) : tt(UI, "next", lang)}</span>
          <ArrowRight className="arrow h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

const TIER_VISUAL = {
  prohibited: { color: "var(--red)", icon: OctagonAlert, label: { en: "Prohibited", de: "Verboten" } },
  high: { color: "var(--red)", icon: ShieldAlert, label: { en: "High-risk", de: "Hochrisiko" } },
  possibly_high: { color: "var(--red)", icon: AlertTriangle, label: { en: "Possibly high-risk", de: "Möglicherweise hochriskant" } },
  gpai_provider: { color: "var(--red)", icon: ShieldAlert, label: { en: "GPAI provider", de: "GPAI-Anbieter" } },
  limited: { color: "var(--blue)", icon: Info, label: { en: "Limited risk", de: "Begrenztes Risiko" } },
  minimal: { color: "var(--blue)", icon: ShieldCheck, label: { en: "Minimal risk", de: "Minimales Risiko" } },
  out_of_scope: { color: "var(--muted-2)", icon: ShieldCheck, label: { en: "Out of scope", de: "Nicht im Anwendungsbereich" } },
};

function DeadlineBadge({ deadline, lang }) {
  if (!deadline) return null;
  const days = daysUntil(deadline.date);
  const isActive = deadline.phase === "active";
  return (
    <div
      data-testid="result-deadline"
      className="inline-flex flex-col rounded-2xl border px-5 py-3 sm:flex-row sm:items-baseline sm:gap-3"
      style={{
        borderColor: isActive ? "var(--red)" : "var(--line)",
        background: isActive ? "var(--blue-50)" : "white",
      }}
    >
      {isActive ? (
        <span className="text-[13px] font-bold tracking-[0.18em] uppercase" style={{ color: "var(--red)" }}>
          {tt(UI, "in_force_now", lang)}
        </span>
      ) : (
        <span className="text-[26px] font-extrabold tracking-tight" style={{ color: "var(--red)" }}>
          {days}
          <span className="ml-1 text-[14px] font-medium" style={{ color: "var(--muted)" }}>
            {tt(UI, "days", lang)}
          </span>
        </span>
      )}
      <span className="text-[13px] font-medium" style={{ color: "var(--muted)" }}>
        {pickLang(deadline.label, lang)}
      </span>
    </div>
  );
}

function PackPreview({ packKey, tier, lang }) {
  if (!packKey) return null;
  const pack = pickLang(PACKS[packKey], lang);
  if (!pack) return null;
  return (
    <div
      data-testid="result-pack-preview"
      className="mt-9 rounded-2xl border-2 bg-white p-6 md:p-8"
      style={{ borderColor: "var(--blue)" }}
    >
      <div className="flex items-baseline justify-between gap-4">
        <p className="eyebrow" style={{ color: "var(--blue)" }}>
          {tt(UI, "what_you_get", lang)}
        </p>
        <span className="text-[13px] font-semibold tracking-tight" style={{ color: "var(--ink)" }}>
          {pack.title}
        </span>
      </div>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="pack-items">
        {pack.items.map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-[14.5px]" style={{ color: "var(--ink-2)" }}>
            <Check className="mt-1 h-3.5 w-3.5 flex-shrink-0" strokeWidth={3} style={{ color: "var(--blue)" }} aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-[12.5px]" style={{ color: "var(--muted-2)" }}>
        {tt(UI, "pack_disclaimer", lang)}
      </p>
    </div>
  );
}

function VantaAnchor({ lang }) {
  return (
    <div
      data-testid="result-vanta-anchor"
      className="mt-6 rounded-2xl border bg-white p-6 md:p-7"
      style={{ borderColor: "var(--line)" }}
    >
      <p className="eyebrow" style={{ color: "var(--muted-2)" }}>
        {tt(UI, "vanta_anchor_title", lang)}
      </p>
      <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
        {tt(UI, "vanta_anchor_body", lang)}
      </p>
    </div>
  );
}

function buildSubscribeMailto({ tier, packKey, lang }) {
  const subject = lang === "de"
    ? `Adheran-Abonnement starten — ${tier}`
    : `Start Adheran subscription — ${tier}`;
  const body = lang === "de"
    ? [
        "Hallo Vishwanath,",
        "",
        `ich möchte das Adheran-Abonnement für die Tier "${tier}" starten (Paket: ${packKey || "n/a"}).`,
        "",
        "Bitte schicken Sie mir die Rechnung und die nächsten Schritte.",
        "",
        "Beste Grüße,",
      ].join("\n")
    : [
        "Hi Vishwanath,",
        "",
        `I'd like to start the Adheran subscription for the "${tier}" tier (pack: ${packKey || "n/a"}).`,
        "",
        "Please send the invoice and next steps.",
        "",
        "Best,",
      ].join("\n");
  const params = new URLSearchParams({ subject, body });
  return `mailto:${SUBSCRIBE_EMAIL}?${params.toString()}`;
}

function ResultView({ answers, onRestart, lang }) {
  const result = useMemo(() => classify(answers), [answers]);
  const visual = TIER_VISUAL[result.tier] || TIER_VISUAL.limited;
  const Icon = visual.icon;
  const isProhibited = result.tier === "prohibited";

  const [contact, setContact] = useState({ name: "", company: "", role: "", email: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const handleContactChange = (field, value) => {
    setContact((c) => ({ ...c, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(contact.email)) {
      setError(tt(UI, "form_invalid_email", lang));
      return;
    }
    setStatus("submitting");

    try {
      const key = "adheran:audit-submissions";
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      list.push({
        contact,
        answers,
        result: { tier: result.tier, headline: pickLang(result.headline, lang) },
        lang,
        ts: new Date().toISOString(),
      });
      localStorage.setItem(key, JSON.stringify(list));
    } catch (_) { /* ignore */ }

    try {
      const res = await fetch(SUBMIT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          contact,
          answers,
          result: {
            tier: result.tier,
            headline: pickLang(result.headline, lang),
            summary: pickLang(result.summary, lang),
          },
          lang,
          submittedAt: new Date().toISOString(),
        }),
      });
      if (!res.ok && res.status !== 202) throw new Error(`HTTP ${res.status}`);
      setStatus("success");
    } catch (_err) {
      setStatus("success");
    }
  };

  const subscribeHref = buildSubscribeMailto({ tier: result.tier, packKey: result.packKey, lang });

  return (
    <section data-testid="section-result" className="mx-auto w-full max-w-2xl">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
          style={{ background: visual.color, color: "white" }}
          aria-hidden="true"
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
        <p
          data-testid="result-tier-label"
          className="text-[12px] font-semibold tracking-[0.18em] uppercase"
          style={{ color: visual.color }}
        >
          {tt(UI, "preliminary", lang)} · {pickLang(visual.label, lang)}
        </p>
      </div>

      <h1
        data-testid="result-headline"
        className="display-h1 balance mt-5 text-3xl sm:text-4xl md:text-5xl"
        style={{ color: "var(--ink)" }}
      >
        {pickLang(result.headline, lang)}
      </h1>

      <p
        data-testid="result-summary"
        className="pretty mt-6 text-[16.5px] leading-relaxed md:text-lg"
        style={{ color: "var(--muted)" }}
      >
        {pickLang(result.summary, lang)}
      </p>

      <div className="mt-8">
        <DeadlineBadge deadline={result.deadline} lang={lang} />
      </div>

      {result.prohibitedTriggers && result.prohibitedTriggers.length > 0 && (
        <div className="mt-9 rounded-2xl border-2 bg-white p-6 md:p-7" style={{ borderColor: "var(--red)" }}>
          <p className="eyebrow" style={{ color: "var(--red)" }}>
            {tt(UI, "prohibited_flagged", lang)}
          </p>
          <ul className="mt-3 space-y-3" data-testid="result-prohibited-list">
            {result.prohibitedTriggers.map((t, idx) => (
              <li key={idx} className="flex items-baseline gap-3">
                <span
                  className="inline-flex w-[88px] flex-shrink-0 text-[13px] font-semibold tracking-tight"
                  style={{ color: "var(--red)" }}
                >
                  {t.article && t.article.code}
                </span>
                <span className="text-[15px]" style={{ color: "var(--ink-2)" }}>
                  {pickLang(t.label, lang)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.carveoutReason && (
        <div className="mt-6 rounded-xl border p-4 text-[14px] leading-relaxed" style={{ borderColor: "var(--line)", background: "var(--tint)" }}>
          <strong style={{ color: "var(--ink)" }}>{tt(UI, "carveout_claim", lang)}</strong>{" "}
          <span style={{ color: "var(--muted)" }}>{pickLang(result.carveoutReason, lang)}.</span>
          <span className="mt-2 block text-[13px]" style={{ color: "var(--muted-2)" }}>
            {tt(UI, "carveout_warn", lang)}
          </span>
        </div>
      )}

      {result.addOn && (
        <div className="mt-6 rounded-xl border p-4 text-[14px] leading-relaxed" style={{ borderColor: "var(--line)", background: "white", color: "var(--muted)" }}>
          <strong style={{ color: "var(--ink)" }}>{tt(UI, "stacked_obligation", lang)}:</strong> {pickLang(result.addOn, lang)}
        </div>
      )}

      {result.annexAreas && result.annexAreas.length > 0 && (
        <div className="mt-9 rounded-2xl border bg-white p-6 md:p-7" style={{ borderColor: "var(--line)" }}>
          <p className="eyebrow" style={{ color: "var(--muted-2)" }}>{tt(UI, "annex_areas_flagged", lang)}</p>
          <ul className="mt-3 space-y-2" data-testid="result-annex-list">
            {result.annexAreas.map((a, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-[15px]" style={{ color: "var(--ink-2)" }}>
                <span
                  aria-hidden="true"
                  className="mt-2 inline-block h-1 w-3 flex-shrink-0"
                  style={{ background: "var(--red)" }}
                />
                {pickLang(a, lang)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.articles && result.articles.length > 0 && (
        <div className="mt-6 rounded-2xl border bg-white p-6 md:p-7" style={{ borderColor: "var(--line)" }}>
          <p className="eyebrow" style={{ color: "var(--muted-2)" }}>{tt(UI, "articles_apply", lang)}</p>
          <ul className="mt-3 space-y-3" data-testid="result-articles">
            {result.articles.map((a) => (
              <li key={a.code} className="flex items-baseline gap-3">
                <span
                  className="inline-flex w-[88px] flex-shrink-0 text-[13px] font-semibold tracking-tight"
                  style={{ color: "var(--blue)" }}
                >
                  {a.code}
                </span>
                <span className="text-[15px]" style={{ color: "var(--ink-2)" }}>
                  {pickLang(a.title, lang)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isProhibited && <PackPreview packKey={result.packKey} tier={result.tier} lang={lang} />}
      {!isProhibited && <VantaAnchor lang={lang} />}

      <div
        data-testid="result-disclaimer"
        className="mt-6 rounded-xl border p-4 text-[13.5px] leading-relaxed"
        style={{ borderColor: "var(--line)", background: "var(--tint)", color: "var(--muted)" }}
      >
        <strong style={{ color: "var(--ink)" }}>{tt(UI, "disclaimer_intro", lang)}</strong>{" "}
        {pickLang(result.disclaimer, lang) || tt(UI, "disclaimer_default", lang)}
      </div>

      <div
        data-testid="result-cta-card"
        className="mt-9 rounded-2xl border bg-white p-6 md:p-8"
        style={{ borderColor: isProhibited ? "var(--red)" : "var(--ink)", borderWidth: isProhibited ? 2 : 1 }}
      >
        <h2 className="display-h2 text-2xl sm:text-3xl" style={{ color: "var(--ink)" }}>
          {isProhibited
            ? tt(UI, "cta_prohibited_title", lang)
            : result.nextStep === "strong"
            ? tt(UI, "cta_strong_title_high", lang)
            : tt(UI, "cta_soft_title", lang)}
        </h2>
        <p className="mt-3 text-[15.5px] leading-relaxed" style={{ color: "var(--muted)" }}>
          {isProhibited
            ? tt(UI, "cta_prohibited_body", lang)
            : result.nextStep === "strong"
            ? tt(UI, "cta_strong_body_high", lang)
            : tt(UI, "cta_soft_body", lang)}
        </p>

        {!isProhibited && result.packKey && (
          <div className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <a
              data-testid="result-subscribe"
              href={subscribeHref}
              className="btn-primary"
            >
              <span>{tt(UI, "start_subscription", lang)}</span>
              <ArrowRight className="arrow h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
            </a>
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="result-cal-direct"
              className="btn-ghost"
            >
              <span>{tt(UI, "book_call", lang)}</span>
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
            </a>
          </div>
        )}

        {status === "success" ? (
          <div
            data-testid="result-success"
            className="mt-6 rounded-xl border p-5"
            style={{ borderColor: "var(--blue)", background: "var(--blue-50)" }}
          >
            <p className="text-[15.5px] font-semibold" style={{ color: "var(--ink)" }}>
              {tt(UI, "success_thanks", lang, { name: contact.name || "" })}
            </p>
            <p className="mt-2 text-[14.5px] leading-relaxed" style={{ color: "var(--muted)" }}>
              {tt(UI, "success_body", lang, { email: SUBSCRIBE_EMAIL })}
            </p>
            <div className="mt-5">
              <a
                href={CAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="result-cal-after"
                className="btn-primary"
              >
                <span>{tt(UI, "success_book_now", lang)}</span>
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              </a>
            </div>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={handleSubmit} data-testid="result-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-[12px] font-medium tracking-[0.14em] uppercase" style={{ color: "var(--muted-2)" }}>
                  {tt(UI, "form_name", lang)}
                </span>
                <input
                  type="text"
                  required
                  value={contact.name}
                  onChange={(e) => handleContactChange("name", e.target.value)}
                  data-testid="result-input-name"
                  className="mt-1.5 w-full rounded-xl border p-3 text-[15.5px] outline-none focus:border-2"
                  style={{ borderColor: "var(--line)", color: "var(--ink)" }}
                />
              </label>
              <label className="block">
                <span className="text-[12px] font-medium tracking-[0.14em] uppercase" style={{ color: "var(--muted-2)" }}>
                  {tt(UI, "form_email", lang)}
                </span>
                <input
                  type="email"
                  required
                  value={contact.email}
                  onChange={(e) => handleContactChange("email", e.target.value)}
                  data-testid="result-input-email"
                  className="mt-1.5 w-full rounded-xl border p-3 text-[15.5px] outline-none focus:border-2"
                  style={{ borderColor: "var(--line)", color: "var(--ink)" }}
                />
              </label>
              <label className="block">
                <span className="text-[12px] font-medium tracking-[0.14em] uppercase" style={{ color: "var(--muted-2)" }}>
                  {tt(UI, "form_company", lang)}
                </span>
                <input
                  type="text"
                  value={contact.company}
                  onChange={(e) => handleContactChange("company", e.target.value)}
                  data-testid="result-input-company"
                  className="mt-1.5 w-full rounded-xl border p-3 text-[15.5px] outline-none focus:border-2"
                  style={{ borderColor: "var(--line)", color: "var(--ink)" }}
                />
              </label>
              <label className="block">
                <span className="text-[12px] font-medium tracking-[0.14em] uppercase" style={{ color: "var(--muted-2)" }}>
                  {tt(UI, "form_role", lang)}
                </span>
                <input
                  type="text"
                  placeholder={tt(UI, "form_role_ph", lang)}
                  value={contact.role}
                  onChange={(e) => handleContactChange("role", e.target.value)}
                  data-testid="result-input-role"
                  className="mt-1.5 w-full rounded-xl border p-3 text-[15.5px] outline-none focus:border-2"
                  style={{ borderColor: "var(--line)", color: "var(--ink)" }}
                />
              </label>
            </div>

            {error && (
              <p data-testid="result-form-error" className="text-[14px]" style={{ color: "var(--red)" }}>
                {error}
              </p>
            )}

            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={status === "submitting"}
                data-testid="result-submit"
                className="btn-primary disabled:opacity-50"
              >
                <span>{status === "submitting" ? tt(UI, "form_sending", lang) : tt(UI, "form_send", lang)}</span>
                <ArrowRight className="arrow h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              </button>
              {!result.packKey && (
                <a
                  href={CAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="result-cal-fallback"
                  className="btn-ghost"
                >
                  <span>{tt(UI, "book_call_alt", lang)}</span>
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
                </a>
              )}
            </div>
            <p className="text-[12.5px]" style={{ color: "var(--muted-2)" }}>
              {tt(UI, "form_privacy", lang)}
            </p>
          </form>
        )}
      </div>

      <div className="mt-10 flex items-center justify-between border-t pt-6" style={{ borderColor: "var(--line)" }}>
        <button
          type="button"
          onClick={onRestart}
          data-testid="audit-restart"
          className="link-u text-[14px]"
          style={{ color: "var(--muted)" }}
        >
          {tt(UI, "start_over", lang)}
        </button>
        <Link to="/" className="link-u text-[14px]" style={{ color: "var(--muted)" }}>
          {tt(UI, "back_home", lang)}
        </Link>
      </div>

      {lang === "de" && tt(UI, "de_beta_notice", lang) && (
        <p className="mt-6 text-[12px]" style={{ color: "var(--muted-2)" }}>
          {tt(UI, "de_beta_notice", lang)}
        </p>
      )}
    </section>
  );
}

export default function Assessment() {
  const [lang, setLangState] = useState(() => detectLang());
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    document.title = lang === "de"
      ? "Adheran — Kostenloses 5-Minuten-Audit zur KI-Verordnung"
      : "Adheran — Free 5-minute AI Act audit";
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const onLangChange = (next) => {
    setLangState(next);
    persistLang(next);
  };

  const visible = useMemo(() => visibleQuestions(answers), [answers]);
  const safeIndex = Math.min(index, Math.max(0, visible.length - 1));
  const question = visible[safeIndex];
  const total = visible.length;

  const setAnswer = (id, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [id]: value };
      if (id === "annex") {
        const annexEmpty =
          !Array.isArray(value) ||
          value.length === 0 ||
          value.includes("none_annex");
        if (annexEmpty) {
          delete next.profiling;
          delete next.carveout;
        }
      }
      if (id === "profiling" && value !== "no") {
        delete next.carveout;
      }
      return next;
    });
  };

  const handleNext = () => {
    if (safeIndex >= total - 1) setShowResult(true);
    else setIndex(safeIndex + 1);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (safeIndex > 0) setIndex(safeIndex - 1);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRestart = () => {
    setAnswers({});
    setIndex(0);
    setShowResult(false);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="App" data-testid="page-assessment">
      <Nav lang={lang} onLangChange={onLangChange} />
      <main>
        <div className="mx-auto max-w-3xl px-6 py-12 md:px-10 md:py-20">
          {!showResult ? (
            <QuestionView
              question={question}
              answers={answers}
              onAnswer={setAnswer}
              onBack={handleBack}
              onNext={handleNext}
              index={safeIndex}
              total={total}
              canBack={safeIndex > 0}
              lang={lang}
            />
          ) : (
            <ResultView answers={answers} onRestart={handleRestart} lang={lang} />
          )}
        </div>
      </main>
    </div>
  );
}
